<script setup>
/* eslint semi: 1 */

import { computed, inject } from 'vue';
import { t } from '@/i18n/i18ns';
import { signInWithRedirect as AmplifySignInWithRedirect } from 'aws-amplify/auth';

import AuthMixin from '@/components/mixins/auth-mixin';
import FormButton from '@/components/common/form/form-button-tmpl.vue';

import { SOCIAL_PROVIDERS } from '@base-shared/utils/constants';
import { useCognitoAuthStore } from '@base-shared/stores/cognito-auth';
import { useLocalStorageStore } from '@base-shared/stores/local-storage';

defineOptions({
  mixins: [AuthMixin]
});

const props = defineProps({
  disabled: {
    type: Boolean,
    default: false
  },
  type: {
    type: String,
    default: 'login', // Can be either 'login' or 'signup'
    validator (value) {
      return ['login', 'signup'].includes(value);
    }
  }
});

const $router = inject('$routerInstance');
const $utils = inject('$utils');

const googleTooltip = computed(() => {
  return props.type === 'login'
    ? t('label.loginWithName', { name: SOCIAL_PROVIDERS.GOOGLE })
    : t('label.signupWithName', { name: SOCIAL_PROVIDERS.GOOGLE });
});

const facebookTooltip = computed(() => {
  return props.type === 'login'
    ? t('label.loginWithName', { name: SOCIAL_PROVIDERS.FACEBOOK })
    : t('label.signupWithName', { name: SOCIAL_PROVIDERS.FACEBOOK });
});

async function signInWith (provider) {
  if ($router.currentRoute.value?.query?.sso_migrate_account) {
    const localStorageStore = useLocalStorageStore();
    const ssoMigrateAccount = $utils.decodeBase64Format($router.currentRoute.value?.query?.sso_migrate_account);
    const targetSSOProvider = ssoMigrateAccount?.provider;
    localStorageStore.setIsMigratingFromSocialAccountToStorage(targetSSOProvider, true);
  }
  await useCognitoAuthStore().configureWithCognitoStage(CONFIG.GLOBAL_STAGE);
  await AmplifySignInWithRedirect({ provider });
}

defineExpose({
  signInWith
});
</script>
<template>
  <div class="flex flex-col gap-2">
    <div class="flex items-center justify-center gap-3">
      <el-tooltip
        :content="googleTooltip"
        :popper-class="'custom-tooltip-popper'"
        :visible-arrow="false"
        effect="light"
        class="item"
        placement="bottom">
        <form-button
          button-theme="light"
          data-component-element-id="sign-in-google-button"
          class="w-fit! p-1! bg-white"
          size="xs"
          type="button"
          :disabled="disabled"
          @click="signInWith(SOCIAL_PROVIDERS.GOOGLE)"
        >
          <template v-if="disabled">
            <BaseIconSvg
              class="opacity-40 w-5! h-5!"
              name="social:google"
            />
          </template>
          <template v-else>
            <BaseIconSvg
              name="social:google"
              class="w-5! h-5!"
            />
          </template>
        </form-button>
      </el-tooltip>

      <el-tooltip
        :content="facebookTooltip"
        :popper-class="'custom-tooltip-popper'"
        :visible-arrow="false"
        effect="light"
        class="item"
        placement="bottom">
        <form-button
          button-theme="light"
          data-component-element-id="sign-in-facebook-button"
          class="w-fit! p-1! bg-white"
          size="xs"
          type="button"
          :disabled="disabled"
          @click="signInWith(SOCIAL_PROVIDERS.FACEBOOK)"
        >
          <template v-if="disabled">
            <BaseIconSvg
              class="opacity-40 w-5! h-5!"
              name="social:facebook"
            />
          </template>
          <template v-else>
            <BaseIconSvg
              class="w-5! h-5!"
              name="social:facebook"
            />
          </template>
        </form-button>
      </el-tooltip>
    </div>

    <div class="relative flex items-center min-[1920px]:mt-3">
      <div class="grow border-t border-white/40"></div>
      <span class="shrink mx-4 text-white max-[1919px]:text-xs min-[1920px]:text-sm">{{ $t('label.or') }}</span>
      <div class="grow border-t border-white/40"></div>
    </div>
  </div>
</template>
